import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendUp, Receipt, Package, Clock, Users, Buildings, Sparkle, Coins } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending_quotation: '#F59E0B',
  quotations_added: '#0EA5E9',
  vendor_approved: '#10B981',
  po_created: '#3B82F6',
  po_released: '#8B5CF6',
  completed: '#059669',
};

const ACTION_LABELS = {
  user_created: 'User created',
  project_code_created: 'Project code created',
  requisition_created: 'Requisition',
  quotation_added: 'Quotation added',
  vendor_approved: 'Vendor approved',
  po_created: 'PO created',
  po_released: 'PO released',
};

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-white border border-border-default rounded p-6" data-testid={testId}>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
        <Icon size={20} className="text-accent-brand" weight="duotone" />
      </div>
      <h3 className="font-semibold text-text-primary text-sm">{label}</h3>
    </div>
    <p className="text-3xl font-bold text-text-primary">{value}</p>
    {sub && <p className="text-xs text-text-tertiary mt-1">{sub}</p>}
  </div>
);

const AnalyticsDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/analytics/dashboard`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !data) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-brand mx-auto"></div>
          <p className="mt-4 text-text-secondary">Loading analytics…</p>
        </div>
      </DashboardLayout>
    );
  }

  const { summary, negotiation_savings, status_counts, top_employees, top_vendors, recent_activity } = data;

  const statusChartData = Object.entries(status_counts || {}).map(([k, v]) => ({
    name: k.replace(/_/g, ' '),
    value: v,
    fill: STATUS_COLORS[k] || '#94A3B8',
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
            Analytics Overview
          </h1>
          <p className="mt-2 text-text-secondary">
            High-level KPIs across the procurement workflow.
          </p>
        </div>

        {/* Summary KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard icon={Receipt} label="Total Requisitions" value={summary.total_requisitions} sub={`${summary.requisitions_last_30d} in last 30 days`} testId="kpi-total-reqs" />
          <StatCard icon={Package} label="Total POs" value={summary.total_pos} testId="kpi-total-pos" />
          <StatCard icon={TrendUp} label="Total Approved Spend" value={`₹${summary.total_spend.toLocaleString('en-IN')}`} sub={`₹${summary.monthly_spend.toLocaleString('en-IN')} this month`} testId="kpi-total-spend" />
          <StatCard icon={Clock} label="Avg Lead Time" value={`${summary.avg_lead_days}d`} sub={`Median ${summary.median_lead_days}d (req → PO released)`} testId="kpi-lead-time" />
          <StatCard
            icon={Coins}
            label="Negotiation Savings"
            value={`₹${(negotiation_savings?.total_savings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            sub={`${negotiation_savings?.negotiations_count || 0} negotiation${(negotiation_savings?.negotiations_count || 0) === 1 ? '' : 's'} · ${negotiation_savings?.avg_savings_pct || 0}% avg saved`}
            testId="kpi-negotiation-savings"
          />
        </div>

        {/* Status breakdown + Recent Activity side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-border-default rounded p-6" data-testid="status-breakdown">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Requisitions by Status</h2>
            {statusChartData.length === 0 ? (
              <p className="text-text-tertiary text-sm">No requisitions yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={statusChartData} dataKey="value" nameKey="name" outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                    {statusChartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white border border-border-default rounded p-6" data-testid="recent-activity">
            <div className="flex items-center gap-2 mb-4">
              <Sparkle size={18} weight="duotone" className="text-accent-brand" />
              <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
            </div>
            {(!recent_activity || recent_activity.length === 0) ? (
              <p className="text-text-tertiary text-sm">No activity yet.</p>
            ) : (
              <ul className="space-y-3 max-h-[280px] overflow-y-auto">
                {recent_activity.map((log) => (
                  <li key={log.id} className="flex items-start gap-3 pb-2 border-b border-border-default last:border-b-0" data-testid={`activity-${log.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary">
                        <span className="font-medium">{log.actor_name}</span>{' '}
                        <span className="text-text-tertiary">{ACTION_LABELS[log.action] || log.action.replace(/_/g, ' ')}</span>
                        {log.entity_label ? <span className="font-medium"> — {log.entity_label}</span> : null}
                      </p>
                      <p className="text-xs text-text-tertiary">{new Date(log.timestamp).toLocaleString()}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Top employees + Top vendors */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-border-default rounded p-6" data-testid="top-employees">
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} weight="duotone" className="text-accent-brand" />
              <h2 className="text-lg font-semibold text-text-primary">Top Employees by Requisitions</h2>
            </div>
            {(!top_employees || top_employees.length === 0) ? (
              <p className="text-text-tertiary text-sm">No requisitions yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={top_employees} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
                  <XAxis type="number" stroke="var(--text-secondary)" />
                  <YAxis type="category" dataKey="name" stroke="var(--text-secondary)" width={120} />
                  <Tooltip />
                  <Bar dataKey="count" fill="var(--accent-brand)" name="Requisitions" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white border border-border-default rounded p-6" data-testid="top-vendors">
            <div className="flex items-center gap-2 mb-4">
              <Buildings size={18} weight="duotone" className="text-accent-brand" />
              <h2 className="text-lg font-semibold text-text-primary">Top Vendors by Approved Spend</h2>
            </div>
            {(!top_vendors || top_vendors.length === 0) ? (
              <p className="text-text-tertiary text-sm">No approved vendors yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface border-b border-border-default">
                    <tr>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Vendor</th>
                      <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Orders</th>
                      <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Total Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_vendors.map((v) => (
                      <tr key={v.vendor_name} className="border-b border-border-default" data-testid={`vendor-${v.vendor_name}`}>
                        <td className="p-3 font-medium text-text-primary">{v.vendor_name}</td>
                        <td className="p-3 text-right text-text-secondary">{v.orders}</td>
                        <td className="p-3 text-right font-semibold text-accent-brand">₹{v.total_spend.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsDashboard;
